/*
 * Mor Furniture — Marqo storefront theme bridge
 * ------------------------------------------------------------------
 * ONE theme-side asset (Dooney/Quickshop CTA-bridge precedent) that wires the
 * Marqo storefront-search widget to three theme-native elements. Loaded from a
 * <script> tag in the theme (layout/theme.liquid or an app-embed hook) AFTER the
 * theme's global.js so the custom elements are registered.
 *
 * Four independently-guarded integrations. Each is a NO-OP when its Marqo hook
 * or its theme element is absent, so this file is safe to load on any page.
 *
 *   1. Quicklook  -> theme <quick-view> modal  (cancelable `marqo:cta.click`)
 *   2. "Displayed at" store card -> theme store-selection UI  (+ Marqo refresh)
 *      + the phone-toolbar "In Showroom" button (v6.1) and its "Displayed at" row (v6.2)
 *   3. Wishlist   -> theme <product-collect> heart  (`marqo:wishlist:rendered`)
 *   4. Subcollection tile strip -> theme <collection-custom-filter> (relocate above grid)
 *
 * Measured grounding (live preview theme 155990032574, 2026-07-14):
 *   - <quick-view> fetches `data-product-url`, injects `#MainContent > section >
 *     product-info` into `[data-drawer="quick-view-drawer"] .quickview-modal__content`,
 *     then open()s the drawer.
 *   - <product-collect> reads attributes product-handle/-img/-title/-sku/-url/
 *     -variant-id in its CONSTRUCTOR, toggles a `collect` attribute, writes
 *     localStorage `__product_wishlist__` (v1.2, max 20), fires `wishlist:change`.
 *     Global theme CSS toggles the two heart <span>s by `[collect]`.
 *   - header-zipcode-locator updates the store IN-PLACE (no reload) via
 *     updateStorePrices(); it persists cookie `mor_store_code` / `mor_store_name`.
 *     (change-zipcode-popup + set-my-store reload; neither is on the grid page.)
 *   - window.MarqoSearch.performSearch(query, forceRefresh) re-runs a query. It must be
 *     given the query: the no-argument form is fatal on /search (see v6 note 2).
 *
 * v4 (2026-07-22) — two operator-confirmed changes to INTEGRATION 2:
 *   1. STORE CARD = COLLECTIONS ONLY. The theme include (PROPOSED-theme.liquid-
 *      include-v2.liquid) now scopes the near-store-filter card + modal + assets to
 *      collection templates. As defense-in-depth, relocateStoreFilter() is a no-op on
 *      the SEARCH surface (grounded on the theme's body `template-search` class) so the
 *      card never surfaces there even if some future include renders it.
 *   2. SWITCH AUTHORITATIVELY OWNS THE ON-DISPLAY FILTER. The "store on display" facet
 *      was removed from the rail (settings v23), so the switch is now the ONLY writer of
 *      variantMetafieldCustomStoreOnDisplay. On load (collections only) reconcileOnDisplay()
 *      heals any persisted on-display filter to the CURRENT mor_store_code — re-scope to
 *      the current physical store, or clear if no physical store (XX / 00 / W-prefix /
 *      absent). This
 *      supersedes v3 syncSwitchState() (which left foreign codes untouched) and self-heals
 *      the stale-?f_cat-after-modal-store-change edge (former P2-3).
 *
 * v5 (2026-07-23) — ADD INTEGRATION 4 (subcollection tile strip). Purely additive: every v4
 *   behavior (INTEGRATION 1/2/3, helpers, cleanup, boot) is unchanged. The native
 *   <collection-custom-filter> tile strip (rendered by a theme include from
 *   snippets/marqo-subcollections.liquid into a hidden wrapper, reading
 *   collection.metafields.custom.sub_collection) was absent on Marqo surfaces because its native
 *   caller (main-collection-product-grid_new1 -> plp-custom-filter-items) is disabled — the same
 *   disabled-section-chain cause as the store card. relocateSubcollections() moves the rendered
 *   strip to just above the Marqo results grid (below the toolbar + active-filter rows), matching
 *   native order (active-facets -> strip -> product grid). COLLECTIONS-ONLY, no-op on
 *   search/PDP/absent, mirroring relocateStoreFilter incl. persistence-across-re-render.
 *
 * v6 (2026-07-29) — STORE CARD COMES TO THE SEARCH SURFACE. Operator reversed the v2/v4
 *   "strict search parity" scoping: search must mirror collections, so the theme include
 *   (PROPOSED-theme.liquid-include-v4.liquid) now renders the card + modal on search too and
 *   INTEGRATION 2 drops its search gates. Three changes, all inside INTEGRATION 2:
 *     1. relocateStoreFilter() + reconcileOnDisplay() no longer no-op on search. Same sidebar
 *        anchor on both surfaces — measured live 2026-07-29 on /search?q=sofa (theme
 *        155990032574): <aside id="marqo-filter-sidebar" class="marqo-filter-sidebar"> with
 *        [.marqo-filter-header, #marqo-filter-sections] children, identical to collections.
 *     2. performSearch() is now SURFACE-AWARE, fixing a latent teardown bug. The widget's
 *        window.MarqoSearch.performSearch is performSearchWrapper(query, forceRefresh); calling
 *        it with NO argument sends query=undefined. On /search that is treated as a NEW query:
 *        the widget clears every applied filter, POSTs without `q`, the API 400s
 *        ("'q' required in body"), and the fatal-error path runs performFullRecovery() which
 *        DELETES window.MarqoSearch — the widget is dead until reload. Measured live
 *        2026-07-29. This was already reachable in v5 (the store-change poll arms on search;
 *        only relocate/reconcile were gated), and the card makes it reachable from every
 *        toggle. v6 passes the live query + forceRefresh on search, and never calls with an
 *        empty query. On collections the same no-arg call is an inert no-op (performSearch
 *        bails on !isSearchPage()), so that surface's behavior is unchanged — see the
 *        collections-refresh note on performSearch().
 *     3. Filter-driven paths no longer call performSearch() at all: FilterManager.updateFilter
 *        -> notifyListeners -> the app's handleFilterChange already re-runs the right search on
 *        BOTH surfaces (debounced query search on /search, collection search on /collections/*).
 *        Verified live 2026-07-29: updateFilter alone on /search re-issued the query, updated
 *        ?f_cat and re-rendered the grid with no bridge call. Only the store-change path — where
 *        no filter moved and the widget cannot see the cookie — still asks for an explicit
 *        re-run.
 *   INTEGRATION 1/3/4 are untouched; the subcollection tile strip stays COLLECTIONS-ONLY
 *   (its include block is still collection-scoped and relocateSubcollections() keeps its gate).
 *
 * v6.1 (2026-10-26) — PHONE "IN SHOWROOM" BUTTON, nothing else. Native's phone toolbar row 1 is
 *   Filters · In Stock | View All · In Showroom. The button (<near-store-filter-mobile>) toggles the same
 *   on-display filter as the drawer's "Displayed at:" switch; only the disabled grid section renders
 *   it, so Marqo phones reached the store controls only inside the Filters drawer. Theme include v5
 *   renders it into #marqo-native-showroom-btn (collections only, with data-on-display-stores);
 *   relocateShowroomButton() moves it after In Stock | View All, and this file's own styles show it
 *   at <=767 in the toolbar slot the Layer-3 MOBILE TOOLBAR block reserves with an empty ::before.
 *   A tap drives applyOnDisplay/clearOnDisplay, the switch's path; both controls follow the APPLIED
 *   filter on every ready/search event, so Clear all turns both off. clearOnDisplay now clears every
 *   applied on-display value (v7's stateless clear), so the switch and the button can never disagree
 *   on "off"; and a zero-hit landing (marqo:search.empty) counts as the first search for reconcile, so
 *   a late reconcile cannot override a shopper's toggle (both as v7). At a web store (XX, 00, W-prefixed, none) or
 *   a store not in the list a tap does nothing, as on native.
 *   This is the BUTTON PART of the reviewed v7 candidate (candidates/showroom-btn-2026-10-19),
 *   ported without v7's automatic In Showroom, store-change refresh() and repricing hook. The
 *   v7 candidate's Layer-3 CSS block lives here instead, so no settings push is needed. It copies
 *   the Layer-3 MOBILE TOOLBAR selector chain to beat its ::before spacer: a later v7 write or the
 *   candidate's settings push must drop one of the two copies.
 *
 * v6.2 (2026-10-26) — PHONE "DISPLAYED AT: <store> x" ROW (decision-queue item 18). While the on-display
 *   filter is on, native phones show a second near-store-filter under toolbar row 1 (wrapper
 *   div.near-store-filter-mobile.medium-hide.large-up-hide; theme CSS hides its switch and shows a close x).
 *   Theme include v6 renders it into #marqo-native-store-row with native's classes; relocateStoreRow() moves
 *   that wrapper into the Marqo header, ensureStyles() puts it on its own line after row 1 at <=767 (theme
 *   CSS hides it at >=768), and it is shown exactly while an on-display value is applied. Its store name is
 *   the theme's store-list-popup-opener (change-store modal) and its x is NearStoreFilter's
 *   cancel-filter-action, which dispatches switcher-toggle {state:false}, handled by onSwitcherToggle.
 *   Because the page now has TWO near-store-filter elements, the card is looked up explicitly (sidebar or its
 *   hidden wrapper) and every switch is kept in step. An in-place store change also rewrites the store name
 *   in both from the theme's store list (the theme's own header-ZIP path writes a name too, so the last
 *   writer wins: its store-name or our ecomm-name; the two differ only in spelling, e.g. 1A).
 */
(function () {
  "use strict";

  if (window.__morMarqoBridgeLoaded) return;
  window.__morMarqoBridgeLoaded = true;

  var BRIDGE_VERSION = "v6.2";

  var DEBUG = !!window.__morBridgeDebug;
  function log() {
    if (DEBUG && window.console) console.log.apply(console, ["[mor-bridge]"].concat([].slice.call(arguments)));
  }

  // ---------------------------------------------------------------- helpers
  function getCookie(name) {
    var parts = document.cookie ? document.cookie.split("; ") : [];
    for (var i = 0; i < parts.length; i++) {
      if (parts[i].indexOf(name + "=") === 0) return parts[i].slice(name.length + 1);
    }
    return null;
  }

  function escAttr(s) {
    return String(s == null ? "" : s).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  }

  // ---------------------------------------------------------------- styles
  // Self-contained CSS so the bridge works standalone (DoD: safe on any page).
  // The wishlist positioning block is ALSO delivered as a separate artifact for
  // the lead/cards teammate to fold into custom_css; keeping it here means the
  // bridge does not depend on collation order.
  var STYLE_ID = "marqo-bridge-styles";
  function ensureStyles() {
    if (document.getElementById(STYLE_ID)) return;
    var css = [
      /* --- wishlist heart: position top-right of the Marqo card image --- */
      ".marqo-product-card-image { position: relative; }",
      ".marqo-product-card-image .marqo-wishlist-theme-native {",
      "  position: absolute; top: .6rem; right: .6rem; z-index: 2;",
      "  width: 3rem; height: 3rem; display: flex; align-items: center; justify-content: center;",
      "  background: none; border: none; cursor: pointer; margin: 0; padding: 0;",
      "}",
      ".marqo-product-card-image .marqo-wishlist-theme-native product-collect { display: inline-flex; line-height: 0; }",
      ".marqo-product-card-image .marqo-wishlist-theme-native product-collect svg { width: 2rem; height: 2rem; }",
      /* self-contained outline/filled toggle (mirrors the theme's global rules,
         so the heart renders correctly even if theme CSS changes) */
      ".marqo-wishlist-theme-native product-collect span:nth-child(2) { display: none; }",
      ".marqo-wishlist-theme-native product-collect[collect] span:nth-child(1) { display: none; }",
      ".marqo-wishlist-theme-native product-collect[collect] span:nth-child(2) { display: inline-flex; }",
      /* NOTE: the "Displayed at" store card + on-display switch are the theme's own
         <near-store-filter> component (styled by custom-facets.css, loaded via the
         theme include). The bridge only relocates it — no bridge CSS for it. */
      /* --- In Showroom button (v6.1): hidden in the Marqo toolbar except at phone widths. At <=767
         it takes the third row-1 slot in place of the Layer-3 MOBILE TOOLBAR block's empty ::before
         spacer (native's phone toolbar ends at 767; 768 keeps the spacer, desktop is untouched). The
         spacer is dropped only while the button is in the header. Its look is the theme's own
         custom-facets.css (.near-store-filter-mobile-component .button, .active). --- */
      ".marqo-results-header near-store-filter-mobile { display: none; }",
      /* v6.2: same for the "Displayed at" row outside the <=767 layout (drawer-portal mode, sub-pixel widths). */
      ".marqo-results-header > #marqo-native-store-row { display: none; }",
      "@media (max-width: 767px) {",
      "  html:not(.marqo-drawer-mobile-portal) body.template-collection .marqo-search-layout .marqo-results-header:has(near-store-filter-mobile)::before { content: none; }",
      "  html:not(.marqo-drawer-mobile-portal) body.template-collection .marqo-search-layout .marqo-results-header near-store-filter-mobile { display: block; order: 3; flex: 0 0 auto; }",
      /* v6.2: the "Displayed at" row takes its own line after row 1 (the header's ::after line break is
         also order 4 and, as a pseudo-element, comes after it), as on native. Theme CSS gives it margin-top
         25px and hides it at >=768 (medium-hide 768-991, large-up-hide >=992). margin-bottom -16px cancels that ::after break's 16px
         margin-top, which native does not have between the row and Sort by: measured at 390 on sofas,
         Sort by moves down 39px when the row appears, on native and on this (55px without it). */
      "  html:not(.marqo-drawer-mobile-portal) body.template-collection .marqo-search-layout .marqo-results-header > #marqo-native-store-row:not([hidden]) { display: block; order: 4; flex: 0 0 100%; margin-bottom: -16px; }",
      "}",
      /* --- quick-view instant-open loading spinner (theme teal) --- */
      ".marqo-qv-loading { display: flex; align-items: center; justify-content: center; width: 100%; min-height: 320px; }",
      ".marqo-qv-spinner { width: 44px; height: 44px; border: 3px solid #e5e5e5; border-top-color: #537075; border-radius: 50%; animation: marqo-qv-spin .8s linear infinite; }",
      "@keyframes marqo-qv-spin { to { transform: rotate(360deg); } }"
    ].join("\n");
    var style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = css;
    document.head.appendChild(style);
  }

  // ============================================================ INTEGRATION 1
  // Quicklook CTA -> theme <quick-view> modal.
  //
  // The theme's Quickview.fetchProduct() fetches the FULL PDP HTML (~2MB, served
  // `private, no-store` so the browser cache never helps) and only shows the drawer
  // AFTER that fetch — a ~3s dead wait on slower connections. Two mitigations, both
  // additive (no fork of the theme element; graceful fallback to fetchProduct()):
  //   (1) Perceived latency: open the drawer + a spinner IMMEDIATELY on click.
  //   (2) Hover/touch prefetch: warm an in-memory cache keyed by product-url so the
  //       click renders from cache (near-instant). Cap 10, abort evicted in-flight.
  var QV_PREFETCH = new Map(); // url -> { promise, text, controller }
  var QV_CACHE_MAX = 10;
  var qvHoverTimer = null;
  var qvHoverUrl = null;

  function initQuickView() {
    document.addEventListener("marqo:cta.click", onCtaClick);
    // Delegated hover (desktop) + touch (mobile) prefetch. No-op off Marqo grids
    // (closest .marqo-product-card is null). Passive/capture so it never blocks.
    document.addEventListener("mouseover", onCardHoverPrefetch, true);
    document.addEventListener("touchstart", onCardTouchPrefetch, { capture: true, passive: true });
  }

  function onCtaClick(e) {
    var detail = e.detail || {};
    var productUrl = detail.productUrl || (detail.handle ? "/products/" + detail.handle : null);
    if (!productUrl) return;

    // Guard: only hijack the CTA if the theme quick-view machinery is present.
    // Otherwise let the widget's default navigation to the PDP proceed.
    var drawer = document.querySelector('[data-drawer="quick-view-drawer"]');
    if (!drawer || !customElements.get("quick-view")) {
      log("quick-view unavailable; letting default navigation proceed");
      return;
    }
    // Resolve the opener BEFORE preventDefault: if the element did not upgrade
    // (no usable fetchProduct), fall through to the widget's default navigation
    // rather than leaving a dead click.
    var qv = ensureQuickViewEl();
    if (!qv || typeof qv.fetchProduct !== "function") {
      log("quick-view did not upgrade; letting default navigation proceed");
      return;
    }
    e.preventDefault();
    openQuickViewFast(qv, drawer, productUrl);
  }

  function ensureQuickViewEl() {
    var qv = document.getElementById("marqo-bridge-quick-view");
    if (!qv) {
      // Create lazily, only after the drawer is confirmed present (callers guard
      // on the drawer first), so the Quickview constructor captures `this.drawer`.
      qv = document.createElement("quick-view");
      qv.id = "marqo-bridge-quick-view";
      qv.style.display = "none";
      document.body.appendChild(qv);
    }
    return qv;
  }

  function openQuickViewFast(qv, drawer, productUrl) {
    var content = drawer.querySelector(".quickview-modal__content");
    // (1) Instant feedback: show the drawer with a spinner right away.
    showDrawerLoading(drawer, content);
    // (2) Render from the prefetch cache (or an in-flight/fresh fetch), else fall
    // back to the theme's own fetchProduct() (spinner already visible).
    getQuickViewHtml(productUrl).then(function (html) {
      if (html && content && renderProductInfo(content, html)) {
        qv.open(); // reuse the theme's open() for focus + thumbnail setup
      } else {
        qv.setAttribute("data-product-url", productUrl);
        qv.fetchProduct();
      }
    });
  }

  function showDrawerLoading(drawer, content) {
    ensureStyles();
    if (content) content.innerHTML = '<div class="marqo-qv-loading"><div class="marqo-qv-spinner"></div></div>';
    drawer.style.display = "flex";
    drawer.classList.add("show", "shadow");
    document.body.style.overflow = "hidden";
  }

  function renderProductInfo(content, html) {
    try {
      var doc = new DOMParser().parseFromString(html, "text/html");
      var pi = doc.querySelector("#MainContent > section > product-info");
      if (!pi) return false;
      content.innerHTML = "";
      content.appendChild(pi.cloneNode(true));
      return true;
    } catch (e) {
      log("renderProductInfo parse failed", e);
      return false;
    }
  }

  function getQuickViewHtml(url) {
    var entry = QV_PREFETCH.get(url);
    if (!entry) {
      prefetchQuickView(url);
      entry = QV_PREFETCH.get(url);
    }
    return entry ? entry.promise.then(function () { return entry.text; }) : Promise.resolve(null);
  }

  function prefetchQuickView(url) {
    if (!url || QV_PREFETCH.has(url)) return;
    // Only prefetch where Quicklook can actually open — otherwise hovering/touching
    // cards would warm 2MB no-store PDP fetches the click will never consume.
    if (!document.querySelector('[data-drawer="quick-view-drawer"]') || !customElements.get("quick-view")) return;
    var controller = typeof AbortController !== "undefined" ? new AbortController() : null;
    var entry = { text: null, controller: controller };
    entry.promise = fetch(url, { credentials: "same-origin", signal: controller ? controller.signal : undefined })
      .then(function (r) { return r.text(); })
      .then(function (t) { entry.text = t; return t; })
      .catch(function () { QV_PREFETCH.delete(url); return null; });
    QV_PREFETCH.set(url, entry);
    // Cap the cache; abort the evicted entry if its fetch is still in flight.
    if (QV_PREFETCH.size > QV_CACHE_MAX) {
      var oldestKey = QV_PREFETCH.keys().next().value;
      var oldest = QV_PREFETCH.get(oldestKey);
      if (oldest && oldest.text === null && oldest.controller) {
        try { oldest.controller.abort(); } catch (e) {}
      }
      QV_PREFETCH.delete(oldestKey);
    }
  }

  function cardProductUrl(target) {
    var card = target && target.closest ? target.closest(".marqo-product-card") : null;
    if (!card) return null;
    var link = card.querySelector('a.marqo-product-card-link, a[href*="/products/"]');
    return link ? link.getAttribute("href") : null;
  }

  function onCardHoverPrefetch(e) {
    var url = cardProductUrl(e.target);
    if (!url || url === qvHoverUrl) return;
    qvHoverUrl = url;
    // Short dwell so a fast sweep across the grid does not fire a 2MB fetch per card.
    clearTimeout(qvHoverTimer);
    qvHoverTimer = setTimeout(function () { prefetchQuickView(url); }, 150);
  }

  function onCardTouchPrefetch(e) {
    var url = cardProductUrl(e.target);
    if (url) prefetchQuickView(url); // no hover on touch; prefetch on first touch
  }

  // ============================================================ INTEGRATION 3
  // Wishlist placeholders -> theme <product-collect> hearts.
  var HEART_OUTLINE =
    '<span><svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 26 26" fill="none">' +
    '<path fill-rule="evenodd" clip-rule="evenodd" d="M12.8166 8.59999C11.5716 7.2058 9.49543 6.83077 7.9355 8.10743C6.37558 9.38409 6.15597 11.5186 7.38098 13.0285L12.8166 18.0624L18.2522 13.0285C19.4772 11.5186 19.2844 9.37066 17.6977 8.10743C16.1109 6.8442 14.0616 7.2058 12.8166 8.59999Z" fill="white" stroke="#537075" stroke-linecap="round" stroke-linejoin="round"/>' +
    "</svg></span>";
  var HEART_FILLED =
    '<span><svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 26 26" fill="none">' +
    '<path fill-rule="evenodd" clip-rule="evenodd" d="M12.8166 8.59999C11.5716 7.2058 9.49543 6.83077 7.9355 8.10743C6.37558 9.38409 6.15597 11.5186 7.38098 13.0285L12.8166 18.0624L18.2522 13.0285C19.4772 11.5186 19.2844 9.37066 17.6977 8.10743C16.1109 6.8442 14.0616 7.2058 12.8166 8.59999Z" fill="#537075" stroke="#537075" stroke-linecap="round" stroke-linejoin="round"/>' +
    "</svg></span>";

  function initWishlist() {
    // The widget dispatches `marqo:wishlist:rendered` on `document` per render
    // batch (detail: {productCount}; no container element). We rescan the whole
    // document each time — the :not([data-mor-mounted]) filter keeps it cheap and
    // idempotent. If a future widget build adds detail.container, honor it as a
    // scoped optimization.
    document.addEventListener("marqo:wishlist:rendered", function (e) {
      var container = e && e.detail && e.detail.container;
      mountWishlists(container && container.querySelectorAll ? container : document);
    });
    // Also upgrade any placeholders already present (e.g. bridge loaded after a batch).
    mountWishlists(document);
  }

  function mountWishlists(root) {
    // Guard: theme wishlist element must be registered.
    if (!customElements.get("product-collect")) {
      log("product-collect not registered; skipping wishlist mount");
      return;
    }
    var scope = root && root.querySelectorAll ? root : document;
    var placeholders = scope.querySelectorAll(".marqo-wishlist-theme-native:not([data-mor-mounted])");
    if (!placeholders.length) return;
    ensureStyles();

    Array.prototype.forEach.call(placeholders, function (ph) {
      ph.setAttribute("data-mor-mounted", "");
      var productUrl = ph.getAttribute("data-product-url") || "";
      var handle = productUrl.split("/products/").pop().split("?")[0].split("#")[0];
      if (!handle) {
        log("wishlist placeholder missing handle; skipping", ph);
        return;
      }
      var variantId = ph.getAttribute("data-variant-id") || "";
      var card = ph.closest ? ph.closest(".marqo-product-card") : null;

      // Prefer placeholder-supplied metadata (widget PR B); fall back to card DOM.
      var img = ph.getAttribute("data-product-img") || deriveImg(card);
      var title = ph.getAttribute("data-product-title") || deriveTitle(card);
      var sku = ph.getAttribute("data-product-sku") || "";

      // Build via innerHTML so attributes are parsed BEFORE the custom element
      // upgrades — ProductCollect reads its attributes in the constructor, so a
      // createElement()+setAttribute() sequence would upgrade with null attrs.
      ph.innerHTML =
        '<product-collect class="wishlist-wrapper-intermediate"' +
        ' product-handle="' + escAttr(handle) + '"' +
        ' product-url="' + escAttr(productUrl) + '"' +
        ' product-variant-id="' + escAttr(variantId) + '"' +
        (img ? ' product-img="' + escAttr(img) + '"' : "") +
        (title ? ' product-title="' + escAttr(title) + '"' : "") +
        (sku ? ' product-sku="' + escAttr(sku) + '"' : "") +
        ">" + HEART_OUTLINE + HEART_FILLED + "</product-collect>";

      // Contain the heart click. The widget nests the wishlist button INSIDE the
      // card image link (.marqo-product-card-link) for all providers; each provider
      // handler is expected to prevent navigation (Swym does the same). Native Mor
      // cards keep <product-collect> OUTSIDE the link, so ProductCollect never had
      // to. This bubble-phase listener on the placeholder runs AFTER ProductCollect's
      // own click->toggle (host element, registered first at upgrade), so the toggle
      // + wishlist:change still fire; we then cancel the anchor's default navigation
      // (preventDefault) and stop the click reaching the anchor (stopPropagation).
      // Clicking anywhere else on the card still navigates.
      ph.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
      });
    });
  }

  function deriveImg(card) {
    if (!card) return "";
    var img = card.querySelector(".marqo-product-image img, .marqo-product-card-image img, img");
    if (!img) return "";
    return img.getAttribute("src") || img.getAttribute("data-src") || "";
  }

  function deriveTitle(card) {
    if (!card) return "";
    var t = card.querySelector(".marqo-product-title-link, .marqo-product-title, [class*='title']");
    return t ? (t.textContent || "").trim() : "";
  }

  // ============================================================ INTEGRATION 2
  // Native "Displayed at" store card + on-display switch.
  //
  // The card + switch + modal are the THEME's own components (near-store-filter /
  // store-list-popup, defined by facets_intermediate.js + store-list-popup.js, styled
  // by custom-facets.css). They are rendered onto the Marqo collection/search
  // templates by a theme include (they were absent because the grid section that
  // rendered them is disabled). The bridge does two things:
  //   (a) RELOCATE the rendered <near-store-filter> to the top of the Marqo sidebar
  //       (its native position within the facets rail).
  //   (b) HOOK the native switch: <custom-switcher> dispatches a bubbling
  //       `switcher-toggle` {state} on toggle. Natively, near-store-filter maps that
  //       to `input[name="filter.v.m.custom.store_on_display"][value=<mor_store_code>]`
  //       + the native facet form — which is ABSENT on the Marqo page (no interference).
  //       We instead drive the widget's own categorical filter on
  //       variantMetafieldCustomStoreOnDisplay = current mor_store_code (proven on the
  //       wire), exactly mirroring the In Stock -> inventory<Store> path.
  // The store-list-popup modal + the store-name link are the native component's own
  // (opener + set-my-store write the full cookie contract incl. mor_warehouse_code,
  // derived as "W"+store-cd[0]). We only keep the store-change -> performSearch hook.
  //
  // v6: the card renders on BOTH the collection and search surfaces (include v4), and the
  // switch AUTHORITATIVELY owns the on-display filter — on load any persisted
  // variantMetafieldCustomStoreOnDisplay value is reconciled to the current store (the facet
  // that used to co-write it was removed in settings v23).
  var STORE_CODE_COOKIE = "mor_store_code";
  var ON_DISPLAY_FIELD = "variantMetafieldCustomStoreOnDisplay";
  var lastStoreCode = null;
  var storePollTimer = null;
  var onDisplayCode = null; // store code currently applied to the on-display filter
  var reconciled = false; // reconcileOnDisplay() runs once per load; cleanup() resets it
  var lastSearchQuery = null; // last query the widget searched for (search surface only)
  var sawSearchResults = false; // a search has landed this load (`marqo:search.results` or, v6.1, `.empty`)
  var showroomBtn = null; // v6.1: the In Showroom button, kept once found (it is moved out of its wrapper)
  var storeRow = null; // v6.2: the "Displayed at" row wrapper, kept once found (it is moved into the header)

  // Search TEMPLATE detection, grounded on the theme's standard `template-{page_type}` body
  // class (verified live: /collections/* -> "template-collection", /search ->
  // "template-search"). v6: no longer gates the store card; it only keeps the subcollection
  // strip off search. Do NOT use it to decide how to call performSearch — see
  // isWidgetSearchPage().
  function isSearchSurface() {
    return !!(document.body && document.body.classList && document.body.classList.contains("template-search"));
  }

  // Whether the WIDGET will accept a performSearch call. Deliberately mirrors the widget's own
  // isSearchPage() (`pathname === "/search"`) rather than reusing the body-class check: the two
  // can disagree — an alternate search template served at /search carries the body class
  // `template-search-<suffix>`, which the classList check misses — and picking the wrong branch
  // there re-arms the exact teardown v6 exists to remove.
  function isWidgetSearchPage() {
    return window.location.pathname === "/search";
  }

  // The query the widget itself last searched for. Latched from `marqo:search.results` (v6.1: or `.empty`, same detail), whose
  // detail carries `state.lastQuery` verbatim — and state.lastQuery is what performSearch
  // compares its argument against to decide "new query -> clear every filter". Two reasons not
  // to derive it from the URL or from `marqo:search.start`: state.lastQuery is set from the
  // RESPONSE's data.query, which the proxy may have rewritten (query prefix, quoting, synonym
  // substitution), and the prefetch-hydration path renders without ever dispatching
  // search.start. Anything other than the results-event value risks being read as a new query,
  // which would clear the shopper's filters and sort — the failure this bridge is avoiding.
  function currentQuery() {
    return lastSearchQuery || "";
  }

  // Web / non-physical store codes (match native _renderStoreItem filter: W*, XX, 00).
  function isWebStore(code) {
    return !code || code === "XX" || code === "00" || String(code).charAt(0) === "W";
  }

  function initStoreCard() {
    document.addEventListener("marqo:search.results", onStoreResults);
    document.addEventListener("marqo:search.empty", onStoreResults);
    document.addEventListener("marqo:ready", onStoreResults);
    // custom-switcher dispatches `switcher-toggle` (bubbles) on toggle.
    document.addEventListener("switcher-toggle", onSwitcherToggle);
    // v6.1: the In Showroom button's own click handler no-ops on the Marqo page; this one drives the filter.
    document.addEventListener("click", onShowroomClick);
    if (document.querySelector(".marqo-filter-sidebar")) onStoreResults();
  }

  function onStoreResults(e) {
    // v6.1: a zero-hit landing (marqo:search.empty) counts too, as in v7, so reconcile runs on the
    // first render and can never fire late over a shopper's own toggle.
    if (e && (e.type === "marqo:search.results" || e.type === "marqo:search.empty")) {
      sawSearchResults = true;
      if (e.detail && typeof e.detail.query === "string") lastSearchQuery = e.detail.query;
    }
    relocateStoreFilter();
    relocateShowroomButton();
    relocateStoreRow();
    reconcileOnDisplay();
    syncOnDisplayControls();
    // Arm the store-change poll once. Module-scoped state (not a closure flag) so
    // cleanup() on marqo:destroy can reset it and a later re-init re-arms.
    if (!storePollTimer && document.querySelector(".marqo-filter-sidebar")) {
      lastStoreCode = getCookie(STORE_CODE_COOKIE);
      storePollTimer = setInterval(pollStore, 1500);
    }
  }

  // Set the native switch's visual state. CustomSwitcher.onClick() self-toggles `.active`
  // for USER clicks on itself (verified in source); this covers programmatic changes and, since
  // v6.2, the OTHER switches when one control is toggled. No-op when no card is present.
  // v6.2: every near-store-filter's switch (the drawer card and the "Displayed at" row, whose switch the
  // theme CSS hides) so they never disagree.
  function setSwitchActive(active) {
    var switches = document.querySelectorAll("near-store-filter custom-switcher");
    Array.prototype.forEach.call(switches, function (sw) {
      if (active) sw.classList.add("active");
      else sw.classList.remove("active");
    });
  }

  // v6.1: the In Showroom button, from the include's hidden wrapper on first use (it is moved out on
  // relocation, so the reference is kept). Null when the include did not render it (search, PDP, ...).
  function findShowroomButton() {
    if (!showroomBtn) showroomBtn = document.querySelector("#marqo-native-showroom-btn near-store-filter-mobile");
    return showroomBtn;
  }

  // The button's visual state, the class native toggles (`.active`; custom-facets.css styles it).
  function setShowroomActive(active) {
    var btn = findShowroomButton();
    if (!btn) return;
    if (active) btn.classList.add("active");
    else btn.classList.remove("active");
  }

  // Every on-display control (drawer switch, row switch, button, row) at once: reconcile, an in-place
  // store change, a button tap, and (v6.2) a switch or x toggle (any OFF; ON at a physical store).
  // syncOnDisplayControls sets them separately (the switch only once reconciled).
  function setOnDisplayControls(active) {
    setSwitchActive(active);
    setShowroomActive(active);
    setStoreRowVisible(active);
  }

  // v6.2: the "Displayed at" row wrapper from include v6. Null when the include did not render it.
  function findStoreRow() {
    if (!storeRow) storeRow = document.getElementById("marqo-native-store-row");
    return storeRow;
  }

  // Shown exactly while an on-display value is applied (the theme CSS keeps it hidden at >=768).
  function setStoreRowVisible(on) {
    var row = findStoreRow();
    if (!row) return;
    row.hidden = !on;
  }

  // v6.2: move the row wrapper into the Marqo header, where ensureStyles() gives it its own line after
  // row 1. Idempotent; a no-op without the header or the row. Visibility stays with setStoreRowVisible().
  function relocateStoreRow() {
    var header = document.querySelector(".marqo-results-header");
    if (!header) return;
    var row = findStoreRow();
    if (!row) return;
    ensureStyles();
    if (row.parentNode !== header) header.appendChild(row);
  }

  // v6.2: on an in-place store change, write the new store's name into every near-store-filter (row and
  // drawer card). NearStoreFilter fills the name only when it connects, and the theme rewrites it only on
  // its header-ZIP / geolocation path (with store-name; we write ecomm-name, the value NearStoreFilter uses,
  // so whichever lands last wins; they differ only in spelling). The name comes from the theme's own store
  // list (getStoreByCode, global.js) and falls back to the mor_store_name cookie the theme writes with the code.
  function updateStoreNames(code) {
    var name = null;
    try {
      var rec = code && typeof window.getStoreByCode === "function" ? window.getStoreByCode(code) : null;
      if (rec && typeof rec === "object" && rec["ecomm-name"]) name = rec["ecomm-name"];
    } catch (e) {
      log("getStoreByCode failed", e);
    }
    if (!name) {
      var raw = getCookie("mor_store_name");
      try { name = raw ? decodeURIComponent(raw) : null; } catch (e) { name = raw; }
    }
    if (!name) return;
    var nodes = document.querySelectorAll("near-store-filter [data-store-name]");
    Array.prototype.forEach.call(nodes, function (n) { n.textContent = name; });
  }

  // AUTHORITATIVE reconcile of the persisted on-display filter to the CURRENT store.
  // Runs ONCE per load on EVERY Marqo surface (v6: collection AND search). Note it does NOT
  // require the card: on a surface where the card did not render, a persisted on-display value
  // is still healed to the current store — the filter state is corrected while setSwitchActive
  // no-ops for want of a switch. That is the intended outcome (a stale foreign store filter
  // should not survive), but it does mean filter state can move with no visible control. The
  // widget restores
  // `?f_cat` into filterManager.state on load; because the "store on display" facet was
  // removed from the rail (settings v23), the ONLY writer of ON_DISPLAY_FIELD is this
  // bridge's switch, so any persisted value means "on-display at <some store>" and is
  // healed to the current mor_store_code:
  //   - physical store  -> reduce persisted values to exactly [current code] (drop any
  //     foreign codes, add current if missing) and turn the switch ON;
  //   - no physical store (XX/00/W*/absent) -> clear ALL persisted values, switch OFF.
  // Supersedes/absorbs v3 syncSwitchState() and self-heals the stale-?f_cat-after-modal-
  // store-change edge (former P2-3): a reload carrying the OLD store's on-display code now
  // re-scopes to the current store instead of leaving a foreign filter applied.
  function reconcileOnDisplay() {
    var fm = window.MarqoSearch && window.MarqoSearch.filterManager;
    if (!fm) return; // filterManager not ready yet; retry on the next results event
    // Wait for the first results event; reconciling at `marqo:ready` is too early. The widget
    // dispatches ready BEFORE its initial search, and on /search that search sees
    // state.lastQuery === "" !== the real query, so it calls filterManager.clearAll() — wiping
    // both the ?f_cat we just reconciled and the URL, while our switch had already been set ON.
    // Waiting means we read the filter state the widget actually ended up with.
    // Not surface-scoped, and that costs something on collections: v5 reconciled at ready
    // unconditionally, whereas now a collection load whose render never happens (missing results
    // container, or a throwing collection search — both return before renderResults dispatches
    // the event) leaves a persisted foreign on-display code unhealed and the switch unsynced.
    // Accepted: that load has no working grid anyway, and a surface-split here would mean two
    // reconcile timings to reason about.
    if (!sawSearchResults) return;
    if (reconciled) return;
    reconciled = true;

    var applied = ((fm.state && fm.state.categorical && fm.state.categorical[ON_DISPLAY_FIELD]) || []).slice();
    var code = getCookie(STORE_CODE_COOKIE);

    if (!applied.length) {
      // Nothing persisted -> switch stays off; nothing to heal.
      onDisplayCode = null;
      setOnDisplayControls(false);
      return;
    }

    if (isWebStore(code)) {
      // No physical store -> clear ALL persisted on-display values (updateFilter toggles).
      applied.forEach(function (v) { fm.updateFilter(ON_DISPLAY_FIELD, "categorical", v); });
      onDisplayCode = null;
      setOnDisplayControls(false);
    } else {
      // Physical store -> reduce persisted values to exactly [current code].
      applied.forEach(function (v) {
        if (v !== code) fm.updateFilter(ON_DISPLAY_FIELD, "categorical", v); // drop foreign
      });
      if (applied.indexOf(code) === -1) fm.updateFilter(ON_DISPLAY_FIELD, "categorical", code); // add current
      onDisplayCode = code;
      setOnDisplayControls(true);
    }

    // No explicit re-query: every branch above that changes the filter goes through
    // updateFilter, and the widget re-runs the search off its own filter-change listener
    // (v6 note 3). A branch that changed nothing needs no re-run — the widget's initial
    // search already reflects the persisted state.
  }

  // Move the theme-rendered <near-store-filter> to the top of the Marqo sidebar.
  // Idempotent + a no-op when the theme include is not present (guard: safe on any
  // page / before the render lands). v6: runs on BOTH surfaces — the sidebar element is
  // the same <aside id="marqo-filter-sidebar" class="marqo-filter-sidebar"> on collection
  // and search, so the card lands at the same place in the rail on either.
  function relocateStoreFilter() {
    var sidebar = document.querySelector(".marqo-filter-sidebar");
    if (!sidebar) return;
    // v6.2: the card explicitly (in the sidebar, or still in its hidden wrapper), never the row's copy.
    var filter = sidebar.querySelector("near-store-filter") || document.querySelector("#marqo-native-store-filter near-store-filter");
    if (!filter) return;
    if (filter.parentNode === sidebar && sidebar.firstElementChild === filter) return;
    filter.hidden = false;
    filter.removeAttribute("hidden");
    sidebar.insertBefore(filter, sidebar.firstChild);
  }

  // Native switch toggled -> drive the widget's store_on_display categorical filter.
  // No performSearch() here: each branch below either changes a filter (and the widget
  // re-runs off its own filter-change listener) or changes nothing (nothing to re-run).
  function onSwitcherToggle(e) {
    var sw = e.target;
    if (!sw || typeof sw.closest !== "function" || !sw.closest("near-store-filter")) return;
    var fm = window.MarqoSearch && window.MarqoSearch.filterManager;
    if (!fm) return;
    var state = !!(e.detail && e.detail.state);
    var code = getCookie(STORE_CODE_COOKIE);
    // Web/non-physical store: match native (no matching on-display value) -> no filter.
    if (isWebStore(code)) {
      clearOnDisplay(fm);
      // v6.2: an OFF toggle (switch or an x) turns every control off. An ON flip of the drawer switch keeps
      // v4's native parity: the switch shows on with no filter, the button and row stay off.
      if (state) { setShowroomActive(false); setStoreRowVisible(false); }
      else setOnDisplayControls(false);
      return;
    }
    if (state) applyOnDisplay(fm, code);
    else clearOnDisplay(fm);
    // v6.2: every control follows, including the drawer switch when the toggle came from the row's x.
    setOnDisplayControls(state);
  }

  // v6.1: move the theme-rendered In Showroom button into the Marqo toolbar, right after the
  // In Stock | View All toggle (native row 1 order; also the tab order). Where the toggle is not in the
  // header (not mounted yet, or a drawer portal took it), append to the header row instead; the next
  // results event moves it once the toggle is there. Idempotent; a no-op without the header or the
  // button. Visibility and placement are CSS (ensureStyles): hidden except at <=767.
  function relocateShowroomButton() {
    var header = document.querySelector(".marqo-results-header");
    if (!header) return;
    var btn = findShowroomButton();
    if (!btn) return;
    ensureStyles();
    var toggle = document.getElementById("marqo-stock-toggle-container");
    if (toggle && header.contains(toggle)) {
      if (toggle.nextElementSibling !== btn) toggle.parentNode.insertBefore(btn, toggle.nextSibling);
    } else if (btn.parentNode !== header) {
      header.appendChild(btn);
    }
  }

  // v6.1: In Showroom button clicked -> the same filter path as the switch, then both controls follow.
  // On/off comes from the APPLIED filter (any on-display value applied = on), not the button's class
  // or the cookie, so a tap always turns off what the grid shows. Turning on where native has no
  // enabled checkbox for the store (a web/non-physical store, or one missing from the include's
  // data-on-display-stores list) does nothing (no filter, no `.active`), as native does. With no list
  // rendered, a physical store applies.
  function onShowroomClick(e) {
    var target = e.target;
    if (!target || typeof target.closest !== "function") return;
    var btn = target.closest("near-store-filter-mobile");
    if (!btn || btn !== showroomBtn) return;
    var face = target.closest(".button"); // native listens on the .button child; match its hit area
    if (!face || !btn.contains(face)) return;
    var fm = window.MarqoSearch && window.MarqoSearch.filterManager;
    if (!fm) return;
    if (appliedOnDisplay(fm).length) {
      clearOnDisplay(fm);
      setOnDisplayControls(false);
      return;
    }
    var code = getCookie(STORE_CODE_COOKIE);
    if (isWebStore(code)) return;
    var listed = onDisplayStoresListed();
    if (listed && listed.indexOf(code) === -1) {
      log("tap ignored:", code, "has nothing on display in this collection");
      return;
    }
    applyOnDisplay(fm, code);
    setOnDisplayControls(true);
  }

  // v6.1: on every ready/search event, show the APPLIED filter on the controls. Other writers move it
  // without the bridge (the drawer's Clear all calls filterManager.clearAll()), and the always-visible
  // button must not stay dark over an unfiltered grid. The button follows from marqo:ready on (the
  // widget has restored ?f_cat by then, so a reload with the filter shows it at once); the switch
  // keeps v6's timing and follows only once reconcile has run (on /search the first search can still
  // clear the restored filter). Side effect at a web store: a switch the shopper flipped on (it
  // self-toggles, no filter) is turned back off by the next search.
  function syncOnDisplayControls() {
    var fm = window.MarqoSearch && window.MarqoSearch.filterManager;
    if (!fm) return;
    var on = appliedOnDisplay(fm).length > 0;
    setShowroomActive(on);
    setStoreRowVisible(on);
    if (reconciled) setSwitchActive(on);
  }

  function appliedOnDisplay(fm) {
    return ((fm.state && fm.state.categorical && fm.state.categorical[ON_DISPLAY_FIELD]) || []).slice();
  }

  // v6.1: the store codes with products on display in this collection (include v5's
  // data-on-display-stores), or null when the include did not render the list.
  function onDisplayStoresListed() {
    var wrapper = document.getElementById("marqo-native-showroom-btn");
    var raw = wrapper ? wrapper.getAttribute("data-on-display-stores") : null;
    if (raw === null) return null;
    return raw.split(",").filter(function (v) { return v; });
  }

  function categoricalHas(fm, field, code) {
    var arr = (fm.state && fm.state.categorical && fm.state.categorical[field]) || [];
    return arr.indexOf(code) > -1;
  }

  // apply/clear return whether they actually moved the filter, so callers can tell an
  // effective change (the widget re-runs the search itself) from a no-change call.
  function applyOnDisplay(fm, code) {
    var changed = false;
    // If a different code was applied (store changed while on), toggle it off first.
    if (onDisplayCode && onDisplayCode !== code && categoricalHas(fm, ON_DISPLAY_FIELD, onDisplayCode)) {
      fm.updateFilter(ON_DISPLAY_FIELD, "categorical", onDisplayCode);
      changed = true;
    }
    if (!categoricalHas(fm, ON_DISPLAY_FIELD, code)) {
      fm.updateFilter(ON_DISPLAY_FIELD, "categorical", code); // updateFilter toggles
      changed = true;
    }
    onDisplayCode = code;
    return changed;
  }

  // v6.1: clears EVERY applied on-display value, not just onDisplayCode (v7's stateless clear), so the
  // switch and the button turn off whatever the grid shows, even a value another writer re-added.
  // (onStoreChanged still calls it only while onDisplayCode is set: v6 store-change behaviour.)
  // updateFilter toggles, so each value is toggled off by passing it again.
  function clearOnDisplay(fm) {
    var applied = appliedOnDisplay(fm);
    applied.forEach(function (v) { fm.updateFilter(ON_DISPLAY_FIELD, "categorical", v); });
    onDisplayCode = null;
    return applied.length > 0;
  }

  // Ask the widget to re-run the CURRENT search. Only the store-change path needs this;
  // filter changes re-run on their own (v6 note 3).
  //
  // SEARCH (/search): pass the latched query + forceRefresh. window.MarqoSearch.performSearch
  //   is performSearchWrapper(query, forceRefresh) — a no-arg call sends query=undefined,
  //   which the widget reads as a brand-new query: it clears every applied filter, POSTs
  //   without `q`, gets a 400, and its fatal-error recovery deletes window.MarqoSearch
  //   outright (measured live 2026-07-29). forceRefresh is required because the query is
  //   UNCHANGED and the widget skips same-query searches without it. If no query has been
  //   latched yet we do nothing: the widget's own first search has not landed, so it is
  //   either still in flight (the call would be dropped by its isSearching guard anyway) or
  //   it failed, and guessing a query is what causes the filter wipe.
  // ELSEWHERE (collections): the widget exposes no collection-search entry point
  //   (performSearch bails on !isSearchPage()), so this call is inert — a store change on a
  //   collection page does not re-run the grid unless a filter also moved. Pre-existing v5
  //   behavior, deliberately left alone in v6; escalated to the lead as
  //   escalations/round8b.json "collections store-change re-run".
  function performSearch() {
    try {
      if (!window.MarqoSearch || typeof window.MarqoSearch.performSearch !== "function") return;
      if (isWidgetSearchPage()) {
        var q = currentQuery();
        if (!q) return; // never hand the widget a query it would read as a new one
        window.MarqoSearch.performSearch(q, true);
        return;
      }
      window.MarqoSearch.performSearch();
    } catch (e) {
      log("performSearch failed", e);
    }
  }

  function pollStore() {
    var code = getCookie(STORE_CODE_COOKIE);
    if (code !== lastStoreCode) {
      lastStoreCode = code;
      onStoreChanged();
    }
  }

  function onStoreChanged() {
    log("store changed ->", lastStoreCode);
    if (!isWebStore(lastStoreCode)) updateStoreNames(lastStoreCode);
    var fm = window.MarqoSearch && window.MarqoSearch.filterManager;
    var changed = false;
    // Re-scope an active on-display filter to the new store (in-place change via the
    // zip locator; the modal Set-as-My-Store reloads, which resets everything). v4:
    // reconcile is once-per-load, so the switch visual is now kept coherent here.
    if (fm && onDisplayCode) {
      if (isWebStore(lastStoreCode)) {
        changed = clearOnDisplay(fm);
        setOnDisplayControls(false);
      } else {
        changed = applyOnDisplay(fm, lastStoreCode);
        setOnDisplayControls(true);
      }
    }
    // A moved filter re-runs the search by itself. Ask for a re-run only when nothing moved:
    // the store cookie is invisible to the widget's filter state, but the per-store stock
    // field the In Stock toggle composes from it is not, so the results are stale without one.
    if (!changed) performSearch();
  }

  // ============================================================ INTEGRATION 4
  // Native subcollection tile strip (<collection-custom-filter>).
  //
  // The strip is the THEME's own component (rendered by the theme include from
  // snippets/marqo-subcollections.liquid into a hidden wrapper #marqo-native-subcollection-tiles,
  // reading collection.metafields.custom.sub_collection; styled by collection-custom-filter-link.css,
  // links driven by a-with-in-stock-filter.js). It was absent on Marqo surfaces because the grid
  // section that natively rendered it (main-collection-product-grid_new1 -> plp-custom-filter-items)
  // is disabled. The bridge RELOCATES the rendered <collection-custom-filter> from the hidden wrapper
  // to just above the Marqo results grid, below the toolbar (.marqo-results-header) + active-filter
  // (.marqo-active-filters-container) rows — matching native order (active-facets -> strip -> grid).
  //
  // Mirrors relocateStoreFilter: moves the LIVE element (no clone), runs on each results event so it
  // persists across widget re-renders. Grounded live (theme 155990032574, /collections/living-room,
  // 2026-07-23): the widget updates #marqo-results-container IN PLACE (same element across
  // performSearch) and preserves foreign sibling nodes in .marqo-search-content, so a strip inserted
  // before #marqo-results-container survives re-render and keeps its position (idempotent guard
  // no-ops on subsequent calls). COLLECTIONS-ONLY, no-op on search/PDP/absent.

  function initSubcollections() {
    document.addEventListener("marqo:search.results", relocateSubcollections);
    document.addEventListener("marqo:ready", relocateSubcollections);
    if (document.querySelector(".marqo-search-content")) relocateSubcollections();
  }

  // Move the theme-rendered <collection-custom-filter> above the Marqo results grid.
  // Idempotent + a no-op when the theme include is not present, the collection has no
  // sub_collection metafield (snippet renders nothing), or there is no Marqo results column
  // (PDP / list-collections index / before the grid renders). Hard no-op on the SEARCH surface
  // so the strip is COLLECTIONS-ONLY even though its include block is already
  // collection-scoped (defense-in-depth). Unlike the store card, the strip has no search
  // counterpart at all: it renders from collection.metafields.custom.sub_collection, which
  // does not exist on a search results page.
  function relocateSubcollections() {
    if (isSearchSurface()) return;
    // Anchor directly on the results grid and insert the strip as its immediate previous
    // sibling, regardless of how the grid is nested. Using grid.parentNode (not a fixed
    // .marqo-search-content child) means the insert never throws NotFoundError if the widget
    // ever wraps the grid deeper, and the strip still lands directly above the products (below
    // the toolbar + active-filter rows, which precede the grid) = native order.
    var grid = document.getElementById("marqo-results-container");
    if (!grid || !grid.parentNode) return; // no Marqo results column (PDP / index / pre-render)
    var strip = document.querySelector("collection-custom-filter");
    if (!strip) return; // include absent, or collection has no sub_collection metafield
    var parent = grid.parentNode;
    // Already positioned as the immediate previous sibling of the grid -> nothing to do.
    if (strip.parentNode === parent && strip.nextElementSibling === grid) return;
    strip.hidden = false;
    strip.removeAttribute("hidden");
    parent.insertBefore(strip, grid);
  }

  // ---------------------------------------------------------------- teardown
  function cleanup() {
    if (storePollTimer) {
      clearInterval(storePollTimer);
      storePollTimer = null; // reset so a later marqo:ready/search.results re-arms the poll
    }
    lastStoreCode = null;
    lastSearchQuery = null;
    sawSearchResults = false;
    var fm = window.MarqoSearch && window.MarqoSearch.filterManager;
    if (fm) clearOnDisplay(fm);
    onDisplayCode = null;
    reconciled = false; // re-arm reconcile for a later SPA re-init
  }

  // ---------------------------------------------------------------- boot
  function boot() {
    initQuickView();
    initWishlist();
    initStoreCard();
    initSubcollections();
    document.addEventListener("marqo:destroy", cleanup);
    log("bridge booted", BRIDGE_VERSION);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
